import React, { useState, useMemo, useEffect, useLayoutEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import './Documents.css';
import { FaPlus, FaChevronDown, FaSearch } from 'react-icons/fa';
import { IoMdClose } from 'react-icons/io';
import { useNavigate } from 'react-router-dom';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import axios from 'axios';
import { useSelector } from 'react-redux';
import { useDocuments } from './context/DocumentsContext.js';

const API_BASE_URL = process.env.REACT_APP_EZTS_API_BASE_URL;
const FTP_BASE_URL = process.env.REACT_APP_EZTS_FTP_BASE_URL;

/**
 * Least important first — hidden as the table container shrinks.
 * Always kept: file_name, operation (download).
 * Thresholds hide early; a layout pass then hides more until scroll is gone.
 */
const COLUMN_PRIORITY = [
  { key: 'create_ts', className: 'col-p7', maxWidth: 1280 },
  { key: 'user_name', className: 'col-p6', maxWidth: 1120 },
  { key: 'file_size', className: 'col-p5', maxWidth: 980 },
  { key: 'file_type', className: 'col-p4', maxWidth: 860 },
  { key: 'dept_name', className: 'col-p3', maxWidth: 760 },
  { key: 'doc_id', className: 'col-p2', maxWidth: 640 },
  { key: 'doc_date', className: 'col-p1', maxWidth: 520 },
];

const formatSize = (size) => {
  if (size > 1024 * 1024) return (size / (1024 * 1024)).toFixed(2) + ' MB';
  if (size > 1024) return (size / 1024).toFixed(2) + ' KB';
  return size + ' B';
};

const Documents = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);
  const [filteredDocuments, setFilteredDocuments] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'descending' });
  const [searchInputs, setSearchInputs] = useState({});
  const [searchVisible, setSearchVisible] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 8;
  const [tsDocError, setDocApiError] = useState(false);
  const [hiddenColumnKeys, setHiddenColumnKeys] = useState([]);
  const tableContainerRef = useRef(null);

  const { startDate,
          setStartDate,
          endDate,
          setEndDate,
          hasFetched,
          setHasFetched,
          documents,
          setDocuments } = useDocuments();
  
  const departments = {
    1: 'Finance',
    2: 'Marketing',
    3: 'Operations',
    4: 'Human Resources',
    5: 'Infomation Technology',
    6: 'Customer Service',
    7: 'Risk Management',
  };

  useEffect(() => {
    const el = tableContainerRef.current;
    if (!el) return undefined;

    const syncFromWidth = (width) => {
      const nextHidden = COLUMN_PRIORITY.filter((col) => width <= col.maxWidth).map(
        (col) => col.key
      );
      setHiddenColumnKeys((prev) =>
        prev.length === nextHidden.length && prev.every((key, i) => key === nextHidden[i])
          ? prev
          : nextHidden
      );
    };

    syncFromWidth(el.clientWidth);

    if (typeof ResizeObserver !== 'function') {
      const onResize = () => syncFromWidth(el.clientWidth);
      window.addEventListener('resize', onResize);
      return () => window.removeEventListener('resize', onResize);
    }

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      const width = entry?.contentRect?.width ?? el.clientWidth;
      syncFromWidth(width);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // After threshold hides, keep dropping lower-priority cols until the table fits.
  useLayoutEffect(() => {
    const el = tableContainerRef.current;
    if (!el) return;

    if (el.scrollWidth <= el.clientWidth + 1) return;

    const nextCol = COLUMN_PRIORITY.find((col) => !hiddenColumnKeys.includes(col.key));
    if (!nextCol) return;

    setHiddenColumnKeys((prev) =>
      prev.includes(nextCol.key) ? prev : [...prev, nextCol.key]
    );
  }, [hiddenColumnKeys, documents, filteredDocuments, currentPage]);

  const fetchDocuments = async (from = "", to = "") => {
    if (!user) return;
    try {
      const response = await axios.get(
        `${API_BASE_URL}document/all?orgId=${user.department?.costCenter?.organization?.orgId}&from=${from}&to=${to}`
      );

      const apiData = Array.isArray(response.data)
        ? response.data
        : Array.isArray(response.data?.data)
          ? response.data.data
          : [];
      console.log("Document API response:", apiData);

      const mapped = apiData.map(doc => ({
        doc_id: doc.docId || "",
        dept_name: doc.department?.deptName || "",
        file_name: doc.fileName || "",
        file_type: doc.fileType || "",
        file_size: doc.fileSize || -1,
        doc_date: doc.docDate || "",
        create_uid: doc.createUid || "",
        user_name: `${doc.owner?.lastName ?? ""} ${doc.owner?.firstName ?? ""}`.trim(),
        create_ts: doc.createTs || "",
        file_url: doc.fileUrl || "",
      }));
      

      setDocuments(mapped);
      setFilteredDocuments(null);
      setHasFetched(true);
    } catch (error) {
      console.error("Error fetching documents:", error);
      setDocApiError(true);
      setTimeout(() => setDocApiError(false), 1500);
    }
  }; 

  const sortedDocuments = useMemo(() => {
    let sortableItems = filteredDocuments ? [...filteredDocuments] : [...documents];
    if (sortConfig.key !== null) {
      sortableItems.sort((a, b) => {
        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];
        if (sortConfig.key === 'file_size') {
          aValue = parseFloat(aValue) || 0;
          bValue = parseFloat(bValue) || 0;
        }
        if (sortConfig.key === 'doc_date') {
          aValue = new Date(aValue);
          bValue = new Date(bValue);
        }
        if (typeof aValue === 'string') {
          aValue = aValue.toLowerCase();
          bValue = bValue.toLowerCase();
        }
        if (aValue < bValue) {
          return sortConfig.direction === 'ascending' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'ascending' ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableItems;
  }, [documents, filteredDocuments, sortConfig]);

  // Pagination
  const totalPages = Math.max(1, Math.ceil(sortedDocuments.length / rowsPerPage));
  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const currentRows = sortedDocuments.slice(startIndex, endIndex);

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handleAdd = () => {
    navigate('/documents/add');
  };

  const buildDownloadUrl = (url) => {
    if (!url) return '';
    const trimmed = url.trim();
    if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
      return trimmed;
    }
    // Relative path from the backend — prefix with the FTP base URL.
    const base = FTP_BASE_URL || '';
    return `${base.replace(/\/+$/, '')}/${trimmed.replace(/^\/+/, '')}`;
  };

  const handleApplyFilter = () => {
    const from = startDate ? startDate.toISOString().split("T")[0] : "";
    const to = endDate ? endDate.toISOString().split("T")[0] : "";
    fetchDocuments(from, to);
  };

  useEffect(() => {
    if (!user) return;
    handleApplyFilter();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.userId]);


  const requestSort = (key) => {
    let direction = 'descending';
    if (sortConfig.key === key && sortConfig.direction === 'descending') {
      direction = 'ascending';
    }
    setSortConfig({ key, direction });
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) {
      return (
        <span className="table-sort-arrows">
          <FaChevronDown className="table-sort-arrow up faded margin-bottom" />
          <FaChevronDown className="table-sort-arrow faded margin-top" />
        </span>
      );
    }
    const isAsc = sortConfig.direction === 'ascending';
    return (
      <FaChevronDown
        className={`table-sort-arrow centered${isAsc ? ' up' : ''}`}
      />
    );
  };

  const handleSearchIconClick = (key) => {
    setSearchVisible((prev) => {
      const allClosed = Object.keys(prev).reduce((acc, k) => {
        acc[k] = false;
        return acc;
      }, {});
      return { ...allClosed, [key]: !prev[key] };
    });
  };

  const handleSearchInputChange = (key, value) => {
    setSearchInputs((prev) => ({ ...prev, [key]: value }));
  };

  const handleSearchInputKeyDown = (key, e) => {
    if (e.key === 'Enter') {
      handleSearch(key);
    } else if (e.key === 'Escape') {
      setSearchVisible((prev) => ({ ...prev, [key]: false }));
    }
  };

  const handleSearch = (key) => {
    const value = searchInputs[key] || '';
    setFilteredDocuments(
      documents.filter(document =>
        String(document[key] || '').toLowerCase().includes(value.toLowerCase())
      )
    );
    setSearchVisible((prev) => ({ ...prev, [key]: false }));
    setCurrentPage(1);
  };

  const handleCloseSearch = (key) => {
    setSearchVisible((prev) => ({ ...prev, [key]: false }));
  };

  const renderSortableHeader = (key, title, extraClass = '') => (
    <th className={`document-table-header sortable${extraClass ? ` ${extraClass}` : ''}`}>
      <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
        <span onClick={() => requestSort(key)} style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
          {title} {getSortIcon(key)}
        </span>
        <span style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
          {searchVisible[key] && (
            <div className="table-search-popover">
              <input
                type="text"
                className="table-search-input"
                value={searchInputs[key] || ''}
                autoFocus
                onChange={e => handleSearchInputChange(key, e.target.value)}
                onKeyDown={e => handleSearchInputKeyDown(key, e)}
                placeholder={t('documents.searchPlaceholder', { field: title })}
              />
              <div className="table-search-btn-row">
                <button className="table-search-btn" onClick={() => handleSearch(key)}>
                  <FaSearch style={{ fontSize: '16px' }} />
                </button>
                <button className="table-search-close-btn" onClick={() => handleCloseSearch(key)}>
                  <IoMdClose style={{ fontSize: '18px' }} />
                </button>
              </div>
            </div>
          )}
          <FaSearch
            className="table-search-icon"
            style={{ cursor: 'pointer', marginLeft: '4px', fontSize: '14px' }}
            onClick={() => handleSearchIconClick(key)}
          />
        </span>
      </span>
    </th>
  );

  const hiddenColumns = COLUMN_PRIORITY.filter((col) =>
    hiddenColumnKeys.includes(col.key)
  );
  const tableClassName = [
    'document-table',
    ...hiddenColumns.map((col) => `hide-${col.className}`),
  ].join(' ');

  return (
    <div className="document-sheets-container">
      <div className="document-sheets-header">
        <h1 className="document-sheets-title">{t('documents.title')}</h1>
      </div>

      <div className="filters-wrapper">
        <div className="filters-container">
          <div className="filter-controls">
            <div className="filter-dates-row">
              <div className="filter-group">
                <label className="filter-label">{t('documents.from')}</label>
                <DatePicker
                  selected={startDate}
                  onChange={date => setStartDate(date)}
                  selectsStart
                  startDate={startDate}
                  endDate={endDate}
                  placeholderText={t('documents.selectStartDate')}
                  className="date-input"
                />
              </div>
              <div className="filter-group">
                <label className="filter-label filter-label-to">{t('documents.to')}</label>
                <DatePicker
                  selected={endDate}
                  onChange={date => setEndDate(date)}
                  selectsEnd
                  startDate={startDate}
                  endDate={endDate}
                  minDate={startDate}
                  placeholderText={t('documents.selectEndDate')}
                  className="date-input"
                />
              </div>
            </div>
            <button className="apply-filter-button" onClick={() => handleApplyFilter()}>
              {t('documents.search')}
            </button>
          </div>
        </div>

        <div className="add-button-container">
          <button className="add-button" onClick={handleAdd}>
            <FaPlus />
            {t('documents.addDocument')}
          </button>
        </div>
      </div>

      <div className="document-table-container" ref={tableContainerRef}>
        <table className={tableClassName}>
          <thead>
            <tr>
              {renderSortableHeader('doc_id', t('documents.columns.docId'), 'col-p2')}
              {renderSortableHeader('dept_name', t('documents.columns.department'), 'col-p3')}
              {renderSortableHeader('file_name', t('documents.columns.fileName'), 'document-col-filename')}
              {renderSortableHeader('file_type', t('documents.columns.fileType'), 'col-p4')}
              {renderSortableHeader('file_size', t('documents.columns.fileSize'), 'col-p5')}
              {renderSortableHeader('doc_date', t('documents.columns.docDate'), 'col-p1')}
              {renderSortableHeader('user_name', t('documents.columns.userName'), 'col-p6')}
              {renderSortableHeader('create_ts', t('documents.columns.createTime'), 'col-p7')}
              <th className="document-table-header document-col-actions">{t('documents.columns.operation')}</th>
            </tr>
          </thead>
          <tbody>
            {currentRows.map((doc) => {
              const downloadUrl = buildDownloadUrl(doc.file_url);
              return (
                <tr key={doc.doc_id}>
                  <td className="document-table-cell col-p2">{doc.doc_id}</td>
                  <td className="document-table-cell col-p3">{doc.dept_name}</td>
                  <td className="document-table-cell document-col-filename">{doc.file_name}</td>
                  <td className="document-table-cell col-p4">{doc.file_type}</td>
                  <td className="document-table-cell col-p5">{formatSize(doc.file_size)}</td>
                  <td className="document-table-cell col-p1">{doc.doc_date}</td>
                  <td className="document-table-cell col-p6">{doc.user_name}</td>
                  <td className="document-table-cell col-p7">{doc.create_ts}</td>
                  <td className="document-table-cell document-col-actions">
                    <a
                      href={downloadUrl}
                      className="document-action-button download"
                      target="_blank"
                      rel="noopener noreferrer"
                      download={doc.file_name}
                    >
                      {t('documents.download')}
                    </a>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="pagination">
        <button 
          onClick={handlePreviousPage} 
          disabled={currentPage === 1}
          className="pagination-arrow"
          aria-label={t('documents.previous')}
        >
          &lt;
        </button>
        <input
          type="number"
          min={1}
          max={totalPages}
          value={currentPage}
          onChange={e => {
            let page = Number(e.target.value);
            if (page < 1) page = 1;
            if (page > totalPages) page = totalPages;
            setCurrentPage(page);
          }}
          className="pagination-input"
        />
        <span className="pagination-total">
          {t('documents.of')} {totalPages}
        </span>
        <button 
          onClick={handleNextPage} 
          disabled={currentPage === totalPages}
          className="pagination-arrow"
          aria-label={t('documents.next')}
        >
          &gt;
        </button>
      </div>
    </div>
  );
};

export default Documents;